import React from 'react';
import { Link as RouterLink, generatePath, useParams } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import ROUTES_PATH from '@/router/routesPathConfig';

import emptyStateImage from '@/pages/company/BankFeedRoute/components/EmptyStates/image.svg';
import type { EmptyStateVariant } from '@/pages/company/BankFeedRoute/components/EmptyStates/computeEmptyVariant';

type EmptyStateCopy = {
  titleId: string;
  defaultTitle: string;
  descriptionId: string;
  defaultDescription: string;
};

const COPY: Record<EmptyStateVariant, EmptyStateCopy> = {
  'no-connections': {
    titleId: 'company.bankFeed.empty.noConnections.title',
    defaultTitle: 'No accounts connected yet',
    descriptionId: 'company.bankFeed.empty.noConnections.description',
    defaultDescription:
      'You don’t have any transactions yet because you didn’t connect to any bank account. Please, go to the Settings to connect accounts or contact your admin',
  },
  'no-transactions': {
    titleId: 'company.bankFeed.empty.noTransactions.title',
    defaultTitle: 'No transactions yet',
    descriptionId: 'company.bankFeed.empty.noTransactions.description',
    defaultDescription:
      'You don’t have any transactions yet because you didn’t sync with your ERP and bank. Please go to the Settings to synchronize data or contact your admin',
  },
  connecting: {
    titleId: 'company.bankFeed.empty.connecting.title',
    defaultTitle: 'Syncing your bank feed',
    descriptionId: 'company.bankFeed.empty.connecting.description',
    defaultDescription:
      "We're connecting to your bank and syncing all transactions. This may take a few minutes — please wait while we finish.",
  },
  'no-filter-results': {
    titleId: 'empty.grid.noResults.title',
    defaultTitle: 'No results found',
    descriptionId: 'empty.grid.noResults.description',
    defaultDescription: 'No results match the selected filters.',
  },
};

// no-connections / no-transactions show the "Open settings" CTA that navigates to the
// bank-feed connections settings page (replaces the former inline Settings link).
const CTA_VARIANTS: readonly EmptyStateVariant[] = ['no-connections', 'no-transactions'];

type EmptyStateProps = {
  variant: EmptyStateVariant;
};

// All BankFeed empty-state screens share the same centered layout (visual + title +
// body). `connecting` shows a spinner in place of the illustration and has no CTA;
// no-connections / no-transactions add the Open settings CTA button; no-filter-results
// is plain text (consumed via AG Grid's noRowsOverlay slot).
const EmptyState: React.FC<EmptyStateProps> = ({ variant }) => {
  const { companyId = '' } = useParams<{ companyId: string }>();
  const settingsPath = generatePath(
    ROUTES_PATH.COMPANY_SETTINGS_BANK_FEED_CONNECTIONS.absolute,
    { companyId },
  );
  const copy = COPY[variant];
  const showCta = CTA_VARIANTS.includes(variant);

  return (
    <Box
      height="100%"
      width="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minWidth={500}
      maxWidth={700}
      m="auto"
      p={1}
      textAlign="center"
    >
      {variant === 'connecting' ? (
        <CircularProgress />
      ) : (
        <Box maxWidth={300} minHeight={0} maxHeight={240}>
          <img src={emptyStateImage} alt="" height="100%" />
        </Box>
      )}
      <Typography
        sx={{ typography: { xl: 'h5', xs: 'h6' }, fontWeight: { xl: 400, xs: 400 } }}
        mt={2}
      >
        <FormattedMessage id={copy.titleId} defaultMessage={copy.defaultTitle} />
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        mb={2}
        sx={{ typography: { xl: 'body1', xs: 'body2' } }}
      >
        <FormattedMessage id={copy.descriptionId} defaultMessage={copy.defaultDescription} />
      </Typography>
      {showCta && (
        <Button
          color="primary"
          variant="contained"
          component={RouterLink}
          to={settingsPath}
          sx={{ borderRadius: '20px' }}
        >
          <FormattedMessage
            id="company.bankFeed.empty.openSettings"
            defaultMessage="Open settings"
          />
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
